import React, { useState, useEffect } from 'react';
import { motion, useSpring, useTransform } from 'framer-motion';
import { Activity, CheckCircle2, Clock, Zap } from 'lucide-react';

const TasksCompletedSection = () => {
  const BASE_COUNT = 17231;
  const [count, setCount] = useState(BASE_COUNT);
  
  const springCount = useSpring(BASE_COUNT, { stiffness: 50, damping: 20, duration: 2 });
  const formattedCount = useTransform(springCount, (value) => Math.floor(value).toLocaleString());

  useEffect(() => {
    const initialDelay = setTimeout(() => {
      springCount.set(BASE_COUNT + 5);
      setCount(prev => prev + 5);
    }, 500);

    const interval = setInterval(() => {
      const increment = Math.floor(Math.random() * 3) + 1;
      setCount(prev => {
        const newValue = prev + increment;
        springCount.set(newValue);
        return newValue;
      });
    }, 2000);

    return () => { clearTimeout(initialDelay); clearInterval(interval); };
  }, [springCount]);

  return (
    <section className="py-24 px-6 bg-[#0A0A12] relative overflow-hidden">
      {/* Subtle grid pattern */}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff06_1px,transparent_1px),linear-gradient(to_bottom,#ffffff06_1px,transparent_1px)] bg-[size:64px_64px]" />
      
      {/* Glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[400px] bg-blue-600/[0.07] rounded-full blur-[120px] pointer-events-none" />

      <div className="max-w-5xl mx-auto relative z-10">
        <div className="flex flex-col items-center text-center">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="inline-flex items-center space-x-2 bg-white/[0.04] border border-white/[0.08] rounded-full px-4 py-1.5 mb-8"
          >
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-green-400" />
            </span>
            <span className="text-blue-300/80 text-sm font-medium tracking-wide uppercase">Live System Activity</span>
          </motion.div>

          <motion.div initial={{ opacity: 0, scale: 0.9 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} className="relative mb-6">
            <motion.h2 className="text-7xl md:text-9xl font-extrabold text-transparent bg-clip-text bg-gradient-to-b from-white via-white to-white/30 tracking-tighter tabular-nums">
              {formattedCount}
            </motion.h2>
            <div className="absolute -right-8 top-4">
              <Activity className="w-8 h-8 text-blue-500/50 animate-pulse" />
            </div>
          </motion.div>

          <motion.h3 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="text-2xl md:text-4xl font-bold text-white mb-6 tracking-tight"
          >
            Tasks Completed by AI Agents <span className="text-blue-400">in the Last 30 Days</span>
          </motion.h3>

          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3 }}
            className="text-lg text-white/40 max-w-2xl leading-relaxed"
          >
            Appointment reminders, insurance, follow-ups and other tasks that human staff didn't have time to do manually.
          </motion.p>
        </div>
      </div>
    </section>
  );
};

export default TasksCompletedSection;
